// Client for the interface's search routes (/api/search/*), which relay the
// hub's order=rules answers. Every call goes through api.ts's request(): auth,
// the single-flight refresh and the sign-out rules stay in one place.
//
// The text is sent exactly as typed (no trim, no case change): the hub's rules
// decide what a query means, and "SIEGn.DE" is not "SIEGN.DE".

import { request } from "./api";

export interface SuggestItem {
  ric: string;
  name: string;
  type: string;
  exchange: string;
  isin: string;
  ticker: string;
  currency: string;
  active?: boolean;
  /**
   * The hub pinned this row as a match of the text (order=rules): an
   * identifier's own matches, an exact code, the equities whose ticker is the
   * text. It is not the user's earlier pick (that ranking is the scored order's).
   */
  pinned?: boolean;
}

/** The `note` of a suggest answer (the hub's, as the terminal reads it). */
export interface SuggestNote {
  isin: string;
  name: string | null;
  type: string | null;
  /** `YYYY-MM-DD`. */
  maturity: string | null;
  /** "active", "inactive", "called" or "matured". */
  status: string;
}

export interface SuggestGroup {
  kind: string;
  items: SuggestItem[];
  total?: number;
}

export interface SuggestAnswer {
  query: string;
  groups: SuggestGroup[];
  /** The hub could not answer in full (a source timed out): more may exist. */
  partial: boolean;
  /** For an ISIN whose master record has no code (a called or matured note): what the record says. */
  note?: SuggestNote | null;
  /** The instrument a complete identifier resolved to; null for a short text. */
  instrument?: any;
}

export interface IsinCandidate {
  ric: string;
  name: string;
  isin: string;
  category: string;
  exchange: string;
  currency: string;
  maturity: string;
  assetState: string;
  issuer: string;
  /** The hub's reading of `assetState` ("AC" is active). */
  active?: boolean;
  contributor?: string;
  /**
   * `true` on a share's primary listing (the hub's IsPrimaryRIC question);
   * absent on every other candidate, and on every bond or note.
   */
  primary?: boolean;
}

/** The instrument an ISIN names (its master record), as /lookup/isin answers it. */
export interface IsinInstrument {
  isin: string;
  name: string;
  type: string;
  permId: string | null;
  /** Null when the master record has no code of its own. */
  ric: string | null;
  assetState: string | null;
  assetStatus: string | null;
  /** "active", "inactive", "called" or "matured". */
  status: string;
  maturity: string | null;
}

export interface IsinAnswer {
  isin: string;
  defaultRic: string;
  candidates: IsinCandidate[];
  partial: boolean;
  instrument?: IsinInstrument | null;
}

/**
 * One row of the full search: shaped like a suggestion's, with LSEG's nulls.
 * `dropped` names why the suggestions would not show it (chain, expired,
 * isin_page, fund_id, not_pair, no_match; never hidden here); `exact` marks a
 * row of an ISIN's exact question (page 0 only).
 */
export interface SearchAllRow {
  ric: string;
  name: string | null;
  isin: string | null;
  exchange: string | null;
  type: string | null;
  ticker: string | null;
  currency: string | null;
  /** false for a listing no longer live. */
  active?: boolean;
  dropped?: string;
  exact?: boolean;
}

/**
 * One page of the full search (the hub's `/api/v1/search/all`, relayed). Pages
 * are 0-based and `size` rows long (50). `total` is LSEG's count (null when it
 * did not say); `pages` how many pages reach it within LSEG's window. Page 0
 * of `all` also carries `categories` (the SearchAllCategoryv3 navigator) and
 * `kinds` (hits per group, only the known ones); an ISIN's page 0 starts with
 * its exact rows and `exact` is their count.
 */
export interface SearchAllAnswer {
  query: string;
  kind: string;
  page: number;
  size: number;
  total: number | null;
  pages: number;
  rows: SearchAllRow[];
  categories?: { label: string; count: number; filter?: string }[];
  kinds?: Partial<Record<string, number>>;
  exact?: number;
}

export interface Pick {
  ric: string;
  name?: string;
  kind?: string;
  isin?: string;
  query?: string;
}

const flag = (bulk: boolean) => (bulk ? "1" : "0");

/**
 * Suggestions for a text. `bulk` is true when resolving a sheet row (the hub
 * answers for a batch), false when driving the task pane.
 */
export function suggest(q: string, bulk = false): Promise<SuggestAnswer> {
  return request<SuggestAnswer>(`/api/search/suggest?q=${encodeURIComponent(q)}&bulk=${flag(bulk)}`);
}

export function lookupIsin(isin: string, bulk = false): Promise<IsinAnswer> {
  return request<IsinAnswer>(`/api/search/isin/${encodeURIComponent(isin)}?bulk=${flag(bulk)}`);
}

/** `page` is 0-based. */
export function searchAll(q: string, kind: string, page: number): Promise<SearchAllAnswer> {
  return request<SearchAllAnswer>(
    `/api/search/all?q=${encodeURIComponent(q)}&kind=${encodeURIComponent(kind)}&page=${page}`
  );
}

/**
 * Tell the interface which result the user chose (it ranks that pick first
 * next time). Fire-and-forget: a failure is dropped and this never throws.
 */
export function tellPick(p: Pick): void {
  try {
    request<void>("/api/search/picks", { method: "POST", body: p }).catch(() => {});
  } catch {
    /* a pick report must never break the caller */
  }
}
